<form [formGroup]="form" (ngSubmit)="confirm()" class="flex flex-col gap-5">
  <!-- Cancellation Reason / Category Selection -->
  <div class="flex flex-col gap-2">
    <app-text size="sm" weight="semibold" color="default">Why are you canceling this load?</app-text>
    <app-radio-card-group
      formControlName="category"
      [options]="categoryOptions"
      layout="list"
    />
  </div>

  <!-- Detail Configuration based on Selected Category -->
  @if (selectedCategory() === 'cancelled_by_us') {
    <div class="flex flex-col gap-4 p-4 rounded-xl bg-surface border border-border">
      <app-form-field label="Internal Reason" [required]="true">
        <app-select
          formControlName="carrierReason"
          [options]="carrierReasonOptions"
          placeholder="Select reason for cancellation"
        />
      </app-form-field>

      <app-form-field label="Explanation / Notes">
        <app-textarea
          formControlName="notes"
          placeholder="Add any internal details, breakdown context, or resourcing notes..."
          [rows]="3"
        />
      </app-form-field>

      <!-- Invoicing Impact Notice -->
      <div class="p-3 rounded-lg bg-surface-high border border-border flex items-start gap-3">
        <lucide-icon name="Info" class="w-4 h-4 text-muted mt-0.5 shrink-0"></lucide-icon>
        <div class="flex flex-col gap-0.5">
          <app-text size="xs" weight="medium" color="default">Billing & Invoicing Impact</app-text>
          <app-text size="xs" color="muted">
            Because this load was cancelled internally by the carrier, it is marked as <span class="font-medium text-foreground">Not Billable</span> (TONU Not Applicable) and will not appear in your Invoicing pipeline.
          </app-text>
        </div>
      </div>
    </div>
  }

  @if (selectedCategory() === 'cancelled_by_counterparty') {
    <div class="flex flex-col gap-4 p-4 rounded-xl bg-surface border border-border">
      <app-form-field label="Upstream Reason" [required]="true">
        <app-select
          formControlName="counterpartyReason"
          [options]="counterpartyReasonOptions"
          placeholder="Select upstream cancellation reason"
        />
      </app-form-field>

      <app-form-field label="Communication & Notes">
        <app-textarea
          formControlName="notes"
          placeholder="Add context on counterparty notification, rate agreement, or cancellation notice..."
          [rows]="3"
        />
      </app-form-field>

      <!-- Invoicing Impact Notice -->
      <div class="p-3 rounded-lg bg-surface-high border border-warning/30 flex items-start gap-3">
        <lucide-icon name="Receipt" class="w-4 h-4 text-warning mt-0.5 shrink-0"></lucide-icon>
        <div class="flex flex-col gap-0.5">
          <app-text size="xs" weight="medium" color="warning">TONU Review Required</app-text>
          <app-text size="xs" color="muted">
            This load will be routed to Invoicing & Billing with status <span class="font-medium text-foreground">TONU Decision Pending</span>. The billing user must review and decide TONU fee eligibility before it can be invoiced.
          </app-text>
        </div>
      </div>
    </div>
  }

  @if (selectedCategory() === 'entry_error') {
    <div class="p-4 rounded-xl bg-surface border border-danger/40 flex items-start gap-3.5">
      <div class="w-8 h-8 rounded-lg bg-surface-high border border-danger/40 flex items-center justify-center shrink-0">
        <lucide-icon name="AlertTriangle" class="w-4 h-4 text-danger"></lucide-icon>
      </div>
      <div class="flex flex-col gap-1">
        <app-text size="sm" weight="semibold" color="danger">Permanent Load Deletion</app-text>
        <app-text size="xs" color="muted">
          This load was created in error or is a duplicate. Deleting it will permanently remove the load document, all stops, and company views. This action cannot be undone.
        </app-text>
      </div>
    </div>
  }

  <!-- Modal Action Footer -->
  <div class="flex items-center justify-end gap-3 pt-2 border-t border-border">
    <app-button variant="ghost" type="button" (click)="close()" [disabled]="isSubmitting()">
      Keep Load
    </app-button>

    @if (selectedCategory() === 'entry_error') {
      <app-button
        variant="danger"
        type="submit"
        icon="Trash2"
        [loading]="isSubmitting()"
        [disabled]="isSubmitting()"
      >
        Permanently Delete
      </app-button>
    } @else if (selectedCategory() === 'cancelled_by_us') {
      <app-button
        variant="danger"
        type="submit"
        icon="Ban"
        [loading]="isSubmitting()"
        [disabled]="isSubmitting()"
      >
        Cancel Load
      </app-button>
    } @else {
      <app-button
        variant="primary"
        type="submit"
        icon="Receipt"
        [loading]="isSubmitting()"
        [disabled]="isSubmitting()"
      >
        Cancel Load & Route to TONU
      </app-button>
    }
  </div>
</form>
